// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { ActionIcon, Box, Button, Card, Flex, Modal, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { createReference, HTTP_HL7_ORG } from '@medplum/core';
import type { ChargeItem, ChargeItemDefinition, CodeableConcept, Encounter, Patient } from '@medplum/fhirtypes';
import { CodeableConceptInput, useMedplum } from '@medplum/react';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useAppointmentTypes } from '../../hooks/useAppointmentTypes';
import {
  applyChargeItemDefinition,
  calculateTotalPrice,
  findActiveChargeItemDefinitionByCpt,
} from '../../utils/chargeitems';
import { getEncounterEmCptOptions } from '../../utils/em-cpt';
import { showErrorNotification } from '../../utils/notifications';
import { withTenantScope } from '../../utils/tenant';
import ChargeItemPanel from './ChargeItemPanel';

const AUTO_CREATED_TAG_SYSTEM = 'http://polarissleep.com/charge-item-source';
const AUTO_CREATED_TAG_CODE = 'auto-created';
const CHARGE_ITEM_MODIFIER_URL = `${HTTP_HL7_ORG}/fhir/StructureDefinition/chargeitem-modifier`;
const CHARGE_ITEM_DX_POINTER_URL = 'http://polarissleep.com/fhir/StructureDefinition/diagnosis-pointer';

function isAutoCreatedChargeItem(chargeItem: ChargeItem): boolean {
  return (
    chargeItem.meta?.tag?.some((t) => t.system === AUTO_CREATED_TAG_SYSTEM && t.code === AUTO_CREATED_TAG_CODE) ?? false
  );
}

interface ChargeItemListProps {
  chargeItems: ChargeItem[];
  updateChargeItems: (chargeItems: ChargeItem[]) => void;
  patient: Patient;
  encounter: Encounter;
  readOnly?: boolean;
  heading?: string;
  addLabel?: string;
  secondary?: boolean;
}

export const ChargeItemList = (props: ChargeItemListProps): JSX.Element => {
  const {
    chargeItems,
    updateChargeItems,
    patient,
    encounter,
    readOnly = false,
    heading = 'Charge Items',
    addLabel = 'Add Charge Item',
    secondary = false,
  } = props;
  const [chargeItemsState, setChargeItemsState] = useState<ChargeItem[]>(chargeItems);
  const [opened, { open, close }] = useDisclosure(false);
  const medplum = useMedplum();
  const { appointmentTypes } = useAppointmentTypes();
  const emCptOptions = getEncounterEmCptOptions(encounter, appointmentTypes);

  useEffect(() => {
    setChargeItemsState(chargeItems);
  }, [chargeItems]);

  const updateChargeItemList = useCallback(
    async (updatedChargeItem: ChargeItem): Promise<void> => {
      const updatedChargeItems = chargeItemsState.map((item) =>
        item.id === updatedChargeItem.id ? updatedChargeItem : item
      );
      updateChargeItems(updatedChargeItems);
    },
    [chargeItemsState, updateChargeItems]
  );

  const deleteChargeItem = useCallback(
    async (chargeItem: ChargeItem): Promise<void> => {
      const updatedChargeItems = chargeItemsState.filter((item) => item.id !== chargeItem.id);
      updateChargeItems(updatedChargeItems);
    },
    [chargeItemsState, updateChargeItems]
  );

  const deleteSecondaryChargeItem = useCallback(
    async (chargeItem: ChargeItem): Promise<void> => {
      try {
        if (chargeItem.id) {
          await medplum.deleteResource('ChargeItem', chargeItem.id);
        }
        await deleteChargeItem(chargeItem);
      } catch (err) {
        showErrorNotification(err);
      }
    },
    [deleteChargeItem, medplum]
  );

  const addChargeItem = useCallback(async (): Promise<void> => {
    open();
  }, [open]);

  const updateSecondaryField = useCallback(
    async (
      chargeItem: ChargeItem,
      field: 'code' | 'description' | 'modifier' | 'units' | 'dxPointer',
      value: string
    ) => {
      const updatedChargeItem: ChargeItem = { ...chargeItem };
      if (field === 'code') {
        updatedChargeItem.code = {
          ...chargeItem.code,
          coding: (chargeItem.code?.coding ?? [{ system: HTTP_HL7_ORG }]).map((coding, index) =>
            index === 0 ? { ...coding, code: value } : coding
          ),
        };
      } else if (field === 'description') {
        updatedChargeItem.code = { ...chargeItem.code, text: value };
      } else if (field === 'units') {
        updatedChargeItem.quantity = { ...chargeItem.quantity, value: Number(value) || 0 };
      } else {
        const otherExtensions = (chargeItem.extension ?? []).filter(
          (extension) => extension.url !== CHARGE_ITEM_MODIFIER_URL && extension.url !== CHARGE_ITEM_DX_POINTER_URL
        );
        if (field === 'modifier' && value) {
          otherExtensions.push({
            url: CHARGE_ITEM_MODIFIER_URL,
            valueCodeableConcept: { coding: [{ code: value, display: value }], text: value },
          });
        }
        if (field === 'dxPointer' && value) {
          otherExtensions.push({ url: CHARGE_ITEM_DX_POINTER_URL, valueString: value });
        }
        updatedChargeItem.extension = otherExtensions;
      }
      if (!updatedChargeItem.id) {
        updateChargeItemList(updatedChargeItem);
        return;
      }
      try {
        const savedChargeItem = await medplum.updateResource(updatedChargeItem);
        updateChargeItemList(savedChargeItem);
      } catch (err) {
        showErrorNotification(err);
      }
    },
    [medplum, updateChargeItemList]
  );

  const handleAddChargeItem = useCallback(
    async (
      cptCode: CodeableConcept | undefined,
      chargeItemDefinition: ChargeItemDefinition | undefined
    ): Promise<void> => {
      if (!cptCode) {
        showErrorNotification('Please select a CPT code');
        return;
      }

      try {
        const newChargeItem: ChargeItem = {
          resourceType: 'ChargeItem',
          status: 'planned',
          subject: createReference(patient),
          context: createReference(encounter),
          occurrenceDateTime: new Date().toISOString(),
          extension: [
            {
              url: `${HTTP_HL7_ORG}/fhir/uv/order-catalog/StructureDefinition/ServiceBillingCode`,
              valueCodeableConcept: cptCode,
            },
          ],
          code: cptCode,
          quantity: {
            value: 1,
          },
          definitionCanonical: chargeItemDefinition?.url ? [chargeItemDefinition.url] : undefined,
        };

        const createdChargeItem = await medplum.createResource(withTenantScope(newChargeItem));
        const appliedChargeItem = await applyChargeItemDefinition(medplum, createdChargeItem);

        updateChargeItems([...chargeItemsState, appliedChargeItem]);
        close();
      } catch (err) {
        showErrorNotification(err);
      }
    },
    [patient, encounter, chargeItemsState, updateChargeItems, medplum, close]
  );

  return (
    <Stack gap={0}>
      <Flex justify="space-between" align="center" mb="md">
        <Text fw={600} size="lg">
          {heading}
        </Text>
        <Button leftSection={<IconPlus size={16} />} onClick={addChargeItem} disabled={readOnly}>
          {addLabel}
        </Button>
      </Flex>

      {secondary && (
        <Text c="dimmed" size="sm" mb="md">
          Additional CPT / HCPCS lines submitted on the same claim. Each line carries its own modifiers, units, and
          diagnosis pointer.
        </Text>
      )}

      {secondary ? (
        <Table.ScrollContainer minWidth={720}>
          <Table withTableBorder verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th />
                <Table.Th>Code</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Modifiers</Table.Th>
                <Table.Th>Units</Table.Th>
                <Table.Th>Dx Pointer</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {chargeItems.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed">No secondary codes on this claim yet.</Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                chargeItems.map((chargeItem, index) => {
                  const coding = chargeItem.code?.coding?.find((item) => item.code) ?? chargeItem.code?.coding?.[0];
                  const modifier =
                    chargeItem.extension?.find((extension) => extension.url === CHARGE_ITEM_MODIFIER_URL)
                      ?.valueCodeableConcept?.coding?.[0]?.code ?? '';
                  const dxPointer =
                    chargeItem.extension?.find((extension) => extension.url === CHARGE_ITEM_DX_POINTER_URL)
                      ?.valueString ?? 'G47.33';
                  const code = coding?.code ?? '';
                  return (
                    <Table.Tr key={chargeItem.id ?? coding?.code}>
                      <Table.Td>{index + 1}</Table.Td>
                      <Table.Td>
                        <Select
                          data={[code].filter(Boolean)}
                          value={code || null}
                          placeholder="Code"
                          disabled={readOnly}
                          onChange={(value) => value && updateSecondaryField(chargeItem, 'code', value)}
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          value={chargeItem.code?.text ?? coding?.display ?? ''}
                          disabled={readOnly}
                          onChange={(event) =>
                            updateSecondaryField(chargeItem, 'description', event.currentTarget.value)
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <Select
                          data={['—', '26', '95', '25', 'RR', 'KX']}
                          value={modifier || '—'}
                          disabled={readOnly}
                          onChange={(value) =>
                            updateSecondaryField(chargeItem, 'modifier', value === '—' ? '' : (value ?? ''))
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          type="number"
                          min={1}
                          value={String(chargeItem.quantity?.value ?? 1)}
                          disabled={readOnly}
                          onChange={(event) => updateSecondaryField(chargeItem, 'units', event.currentTarget.value)}
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          value={dxPointer}
                          disabled={readOnly}
                          onChange={(event) => updateSecondaryField(chargeItem, 'dxPointer', event.currentTarget.value)}
                        />
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon
                          aria-label="Remove secondary code"
                          color="red"
                          disabled={readOnly}
                          variant="subtle"
                          onClick={() => deleteSecondaryChargeItem(chargeItem).catch(() => undefined)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  );
                })
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      ) : (
        <>
          {chargeItems.length > 0 && (
            <Stack gap="md">
              {chargeItems.map((chargeItem: ChargeItem) => (
                <ChargeItemPanel
                  key={chargeItem.id}
                  chargeItem={chargeItem}
                  onChange={updateChargeItemList}
                  onDelete={deleteChargeItem}
                  readOnly={readOnly || isAutoCreatedChargeItem(chargeItem)}
                  emCptOptions={emCptOptions}
                />
              ))}
            </Stack>
          )}
          {chargeItems.length === 0 && (
            <Card withBorder shadow="sm">
              <Stack gap="md" align="center">
                <Text c="dimmed">No charge items available</Text>
              </Stack>
            </Card>
          )}
        </>
      )}

      {secondary && (
        <Flex justify="space-between" align="center" mt="md" gap="md" wrap="wrap">
          <Button variant="subtle" leftSection={<IconPlus size={16} />} onClick={addChargeItem} disabled={readOnly}>
            {addLabel}
          </Button>
          <Flex gap="xl">
            <Text c="dimmed" size="sm">
              Secondary lines <b>{chargeItems.length}</b>
            </Text>
            <Text c="dimmed" size="sm">
              Secondary charge <b>${calculateTotalPrice(chargeItems).toFixed(2)}</b>
            </Text>
          </Flex>
        </Flex>
      )}

      <AddChargeItemModal opened={opened && !readOnly} onClose={close} onSubmit={handleAddChargeItem} />
    </Stack>
  );
};

interface AddChargeItemModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (cptCode: CodeableConcept | undefined, chargeItemDefinition: ChargeItemDefinition | undefined) => void;
}

function AddChargeItemModal({ opened, onClose, onSubmit }: AddChargeItemModalProps): JSX.Element {
  const medplum = useMedplum();
  const [cptCode, setCptCode] = useState<CodeableConcept | undefined>();
  const [chargeItemDefinition, setChargeItemDefinition] = useState<ChargeItemDefinition | undefined>();
  const [isResolvingDefinition, setIsResolvingDefinition] = useState(false);

  const handleSubmit = useCallback(() => {
    onSubmit(cptCode, chargeItemDefinition);
    // Clear all state
    setCptCode(undefined);
    setChargeItemDefinition(undefined);
  }, [cptCode, chargeItemDefinition, onSubmit]);

  const handleClose = useCallback(() => {
    // Clear all state
    setCptCode(undefined);
    setChargeItemDefinition(undefined);
    onClose();
  }, [onClose]);

  let definitionPlaceholder = 'Select a CPT code to load pricing';
  if (isResolvingDefinition) {
    definitionPlaceholder = 'Looking up charge item definition...';
  } else if (cptCode) {
    definitionPlaceholder = chargeItemDefinition ? 'Pricing configured' : 'No pricing configured (can still be added)';
  }

  const handleCptCodeChange = useCallback(
    async (value: CodeableConcept | undefined): Promise<void> => {
      setCptCode(value);
      setChargeItemDefinition(undefined);

      const selectedCode = value?.coding?.[0]?.code;
      if (!selectedCode) {
        return;
      }

      setIsResolvingDefinition(true);
      try {
        const definition = await findActiveChargeItemDefinitionByCpt(medplum, selectedCode);
        setChargeItemDefinition(definition);
      } catch (error) {
        showErrorNotification(error);
      } finally {
        setIsResolvingDefinition(false);
      }
    },
    [medplum]
  );

  return (
    <Modal opened={opened} onClose={handleClose} title="Add Charge Item" size="md">
      <Stack gap="md">
        <CodeableConceptInput
          binding="http://www.ama-assn.org/go/cpt/vs"
          label="CPT Code"
          name="cptCode"
          path="ChargeItem.code"
          placeholder="Search for CPT code..."
          required
          onChange={(value) => {
            handleCptCodeChange(value).catch(() => undefined);
          }}
        />

        <Box>
          <Text size="sm" fw={500} mb={5}>
            Charge Item Definition
          </Text>
          <TextInput
            value={chargeItemDefinition?.title || chargeItemDefinition?.code?.coding?.[0]?.display || ''}
            placeholder={definitionPlaceholder}
            readOnly
          />
        </Box>

        <Flex justify="flex-end" gap="sm" mt="md">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!cptCode || isResolvingDefinition}>
            Add Charge Item
          </Button>
        </Flex>
      </Stack>
    </Modal>
  );
}
